<template>
    <div>
        <v-btn-toggle v-model="undo_state" dense class="mr-3 mb-3">
            <v-btn @click="undo" :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-undo</v-icon>
            </v-btn>
            <v-btn @click="redo" :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-redo</v-icon>
            </v-btn>
        </v-btn-toggle>
        <!-- Toggle buttons for formatting -->
        <v-btn-toggle dense class="mr-3 mb-3">
            <v-btn v-for="item in formats" :key="item.value" @click="applyFormat(item.value)"
                :input-value="isActive(item.value)" :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">{{ item.icon }}</v-icon>
            </v-btn>
            <v-btn @click="applyFormat('highlight')" :input-value="isActive('highlight')"
                :small="$vuetify.breakpoint.xsOnly">
                <v-row align="center" class="flex-column" justify="center">
                    <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-format-color-highlight</v-icon>
                    <v-sheet tile style="margin-top: -4px;" height="4" width="26" color="#fbf100">
                    </v-sheet>
                </v-row>
            </v-btn>
        </v-btn-toggle>
        <v-btn-toggle dense class="mr-3 mb-3">
            <v-btn @click="applyFormat('heading-1')" :input-value="isActiveHeading({ level: 1 })"
                :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-format-header-1</v-icon>
            </v-btn>
            <v-btn @click="applyFormat('heading-2')" :input-value="isActiveHeading({ level: 2 })"
                :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-format-header-2</v-icon>
            </v-btn>
            <v-btn @click="applyFormat('heading-3')" :input-value="isActiveHeading({ level: 3 })"
                :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-format-header-3</v-icon>
            </v-btn>
        </v-btn-toggle>

        <v-btn-toggle dense class="mr-3 mb-3">
            <v-btn @click="applyFormat('align-left')" :input-value="isActive({ textAlign: 'left' })"
                :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-format-align-left</v-icon>
            </v-btn>
            <v-btn @click="applyFormat('align-center')" :input-value="isActive({ textAlign: 'center' })"
                :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-format-align-center</v-icon>
            </v-btn>
            <v-btn @click="applyFormat('align-right')" :input-value="isActive({ textAlign: 'right' })"
                :small="$vuetify.breakpoint.xsOnly">
                <v-icon :small="$vuetify.breakpoint.xsOnly">mdi-format-align-right</v-icon>
            </v-btn>
        </v-btn-toggle>

        <!-- Task list controls -->
        <v-btn-toggle dense class="mr-3 mb-3">
            <v-btn @click="applyFormat('task-list')" :input-value="isActive('taskList')"
                :small="$vuetify.breakpoint.xsOnly">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon :small="$vuetify.breakpoint.xsOnly" v-bind="attrs" v-on="on">mdi-format-list-checks</v-icon>
                    </template>
                    <span>Aufgabenliste</span>
                </v-tooltip>
            </v-btn>
            <v-btn @click="indentTaskItem()" :disabled="!canIndentTaskItem()"
                :small="$vuetify.breakpoint.xsOnly">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon :small="$vuetify.breakpoint.xsOnly" v-bind="attrs" v-on="on">mdi-format-indent-increase</v-icon>
                    </template>
                    <span>Aufgabenpunkt einrücken</span>
                </v-tooltip>
            </v-btn>
            <v-btn @click="outdentTaskItem()" :disabled="!canOutdentTaskItem()"
                :small="$vuetify.breakpoint.xsOnly">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon :small="$vuetify.breakpoint.xsOnly" v-bind="attrs" v-on="on">mdi-format-indent-decrease</v-icon>
                    </template>
                    <span>Aufgabenpunkt ausrücken</span>
                </v-tooltip>
            </v-btn>
        </v-btn-toggle>

        <!-- Table controls dropdown -->
        <v-menu
            offset-y
            open-on-hover
            :open-delay="100"
            :close-delay="300"
            :close-on-content-click="false"
            transition="slide-y-transition"
        >
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    text
                    outlined
                    dense
                    :small="$vuetify.breakpoint.xsOnly"
                    v-bind="attrs"
                    v-on="on"
                    :input-value="isTableActive()"
                    class="table-btn mr-3 mb-sm-3 mb-1"
                >
                    <v-icon left :small="$vuetify.breakpoint.xsOnly" class="mr-2">mdi-table</v-icon>
                    <span class="text-button">Tabelle</span>
                    <v-icon small class="ml-1">mdi-chevron-down</v-icon>
                </v-btn>
            </template>
            
            <v-card>                
                <v-card-text class="pa-0">
                    <v-list dense>
                        <!-- Insert table section -->
                        <v-list-group>
                            <template v-slot:activator>
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-plus</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Tabelle einfügen</v-list-item-title>
                                </v-list-item-content>
                            </template>
                            
                            <div class="table-grid-container px-4 pt-3 pb-4">
                                <div class="table-grid-dimensions mb-2">
                                    {{ hoveredRows }}×{{ hoveredCols }} Tabelle
                                </div>
                                <div class="table-grid-selector">
                                    <div 
                                        v-for="row in 8" 
                                        :key="`row-${row}`" 
                                        class="table-grid-row"
                                    >
                                        <div 
                                            v-for="col in 8" 
                                            :key="`col-${col}`"
                                            class="table-grid-cell"
                                            :class="{ 
                                                'cell-highlighted': isHighlighted(row, col),
                                                'with-header': row === 1 && withHeaderRow && col <= hoveredCols && hoveredRows >= row
                                            }"
                                            @mouseover="updateHoveredCell(row, col)"
                                            @click="insertTableFromGrid(row, col)"
                                        ></div>
                                    </div>
                                </div>
                                <v-checkbox
                                    v-model="withHeaderRow"
                                    dense
                                    hide-details
                                    label="Mit Kopfzeile"
                                    class="mt-2 header-checkbox"
                                ></v-checkbox>
                            </div>
                        </v-list-group>
                        
                        <!-- Row operations -->
                        <v-list-group :disabled="!isTableActive()" :class="{ 'disabled-group': !isTableActive() }">
                            <template v-slot:activator>
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-row</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Zeilen</v-list-item-title>
                                </v-list-item-content>
                            </template>
                            
                            <v-list-item @click="tableAction('addRowBefore')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-row-plus-before</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Zeile davor einfügen</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-list-item @click="tableAction('addRowAfter')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-row-plus-after</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Zeile danach einfügen</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-list-item @click="tableAction('deleteRow')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-row-remove</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Zeile löschen</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-list-item @click="tableAction('toggleHeaderRow')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-header-row</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Kopfzeile umschalten</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-group>
                        
                        <!-- Column operations -->
                        <v-list-group :disabled="!isTableActive()" :class="{ 'disabled-group': !isTableActive() }">
                            <template v-slot:activator>
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-column</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Spalten</v-list-item-title>
                                </v-list-item-content>
                            </template>
                            
                            <v-list-item @click="tableAction('addColumnBefore')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-column-plus-before</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Spalte davor einfügen</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-list-item @click="tableAction('addColumnAfter')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-column-plus-after</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Spalte danach einfügen</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-list-item @click="tableAction('deleteColumn')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-column-remove</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Spalte löschen</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-list-item @click="tableAction('toggleHeaderColumn')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-column</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Kopfspalte umschalten</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-group>
                        
                        <!-- Cell operations -->
                        <v-list-group :disabled="!isTableActive()" :class="{ 'disabled-group': !isTableActive() }">
                            <template v-slot:activator>
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-crop-square</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Zellen</v-list-item-title>
                                </v-list-item-content>
                            </template>
                            
                            <v-list-item @click="tableAction('mergeCells')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-merge-cells</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Zellen verbinden</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-list-item @click="tableAction('splitCell')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-split-cell</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Zelle teilen</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            
                            <v-list-item @click="tableAction('toggleHeaderCell')">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small>mdi-table-eye</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Kopfzelle umschalten</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-group>
                        
                        <!-- Table management -->
                        <v-divider v-if="isTableActive()"></v-divider>
                        <v-list-item @click="tableAction('deleteTable')" :disabled="!isTableActive()" :class="{ 'disabled-item': !isTableActive() }">
                            <v-list-item-icon class="mr-2">
                                <v-icon small color="red">mdi-delete</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="red--text">Tabelle löschen</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-menu>

        <slot name="after-controls"></slot>

    </div>
</template>

<script>

export default {

    props: ['editor'],

    data() {
        return {
            undo_state: null,
            formats: [
                { value: 'bold', icon: 'mdi-format-bold' },
                { value: 'italic', icon: 'mdi-format-italic' },
                { value: 'underline', icon: 'mdi-format-underline' },
            ],
            hoveredRows: 3,
            hoveredCols: 3,
            withHeaderRow: true,
        }
    },

    watch: {
        undo_state(value) {
            if (value === 0 || value === 1) {
                this.$nextTick(() => {
                    this.undo_state = null;
                });
            }
        }
    },

    methods: {
        
        isActive(format) {
            if (!this.editor) return false;
            if (format === 'highlight') {
                return this.editor.isActive(format, { color: '#faf594' });
            }
            return this.editor.isActive(format);
        },

        isActiveHeading(level) {
            if (!this.editor) return false;
            return this.editor.isActive('heading', level);
        },

        canIndentTaskItem() {
            if (!this.editor) return false;
            return this.editor.can().sinkListItem('taskItem');
        },

        canOutdentTaskItem() {
            if (!this.editor) return false;
            return this.editor.can().liftListItem('taskItem');
        },

        indentTaskItem() {
            if (!this.editor) return;
            this.editor.chain().focus().sinkListItem('taskItem').run();
        },

        outdentTaskItem() {
            if (!this.editor) return;
            this.editor.chain().focus().liftListItem('taskItem').run();
        },

        redo() {
            this.editor.chain().focus().redo().run();
        },
        undo() {
            this.editor.chain().focus().undo().run();
        },

        // Table methods
        isTableActive() {
            if (!this.editor) return false;
            return this.editor.isActive('table');
        },
        
        insertTable(rows, cols, withHeaderRow) {
            if (!this.editor) return;
            this.editor.chain().focus().insertTable({ rows, cols, withHeaderRow }).run();
        },
        
        tableAction(action) {
            if (!this.editor) return;
            this.editor.chain().focus()[action]().run();
        },

        applyFormat(format) {
            if (!this.editor) return;

            switch (format) {
                case 'bold':
                    this.editor.chain().focus().toggleBold().run();
                    break;
                case 'italic':
                    this.editor.chain().focus().toggleItalic().run();
                    break;
                case 'underline':
                    this.editor.chain().focus().toggleUnderline().run();
                    break;
                case 'highlight':
                    this.editor.chain().focus().toggleHighlight({ color: '#faf594' }).run();
                    break;
                case 'align-left':
                    this.editor.chain().focus().setTextAlign('left').run();
                    break;
                case 'align-center':
                    this.editor.chain().focus().setTextAlign('center').run();
                    break;
                case 'align-right':
                    this.editor.chain().focus().setTextAlign('right').run();
                    break;
                case 'heading-1':
                    this.editor.chain().focus().toggleHeading({ level: 1 }).run();
                    break;
                case 'heading-2':
                    this.editor.chain().focus().toggleHeading({ level: 2 }).run();
                    break;
                case 'heading-3':
                    this.editor.chain().focus().toggleHeading({ level: 3 }).run();
                    break;
                case 'task-list':
                    this.editor.chain().focus().toggleTaskList().run();
                    break;
                default:
                    console.log('Unknown format:', format);
            }
        },

        isHighlighted(row, col) {
            if (!this.editor) return false;
            return row <= this.hoveredRows && col <= this.hoveredCols;
        },

        updateHoveredCell(row, col) {
            this.hoveredRows = row;
            this.hoveredCols = col;
        },

        insertTableFromGrid(row, col) {
            if (!this.editor) return;
            this.editor.chain().focus().insertTable({ rows: row, cols: col, withHeaderRow: this.withHeaderRow }).run();
        },
    }
}
</script>

<style scoped>
/* Table button styling to match v-btn-toggle */
.table-btn {
    background-color: #FAFAFA !important;
    border-color: rgba(0, 0, 0, 0.12) !important;
    box-shadow: none !important;
    color: rgba(0, 0, 0, 0.87) !important;
    height: 36px;
    min-width: 120px;
}

.table-btn:hover {
    background-color: #F5F5F5 !important;
    border-color: rgba(0, 0, 0, 0.26) !important;
}

/* Disabled list group styling */
.disabled-group >>> .v-list-group__header {
    opacity: 0.6;
    pointer-events: none;
}

/* Disabled list item styling */
.disabled-item {
    opacity: 0.6;
    pointer-events: none;
}

/* Table grid selector styles */
.table-grid-container {
    width: 240px;
}

.table-grid-dimensions {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.87);
    font-weight: 500;
}

.table-grid-selector {
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    background-color: #fff;
    padding: 2px;
}

.table-grid-row {
    display: flex;
    flex-direction: row;
}

.table-grid-cell {
    width: 24px;
    height: 24px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    margin: 1px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.cell-highlighted {
    background-color: #1976D2;
    border-color: #1976D2;
}

.with-header {
    background-color: #64B5F6;
    border-color: #1976D2;
}

.header-checkbox {
    margin-left: 4px;
}
</style>